/**
 * ONE TAP — the bindable action table.
 *
 * Every physical input the game reads goes through here. Nothing else in the client is
 * allowed to compare against a raw `KeyboardEvent.code`, because the moment one place
 * hard-codes `'KeyW'` the rebinding screen starts lying to the player.
 *
 * An *input code* is a single string that names one physical control:
 *
 *   keyboard   `KeyboardEvent.code` verbatim — `KeyW`, `Space`, `ShiftLeft`, `Digit1`
 *   mouse      `Mouse0` (left) `Mouse1` (middle) `Mouse2` (right) `Mouse3` `Mouse4`
 *   wheel      `WheelUp` / `WheelDown`
 *   gamepad    `Pad0` … `Pad19`, indexes into the W3C Standard Gamepad button array
 *
 * One string namespace means a binding table is a plain `Record<ActionId, string[]>`,
 * which serialises to localStorage for free and survives a schema bump by validation
 * rather than by migration code.
 *
 * The analogue sticks are deliberately *not* bindable: left stick moves, right stick
 * looks, on every pad ever made. Making that configurable would be complexity nobody
 * asked for.
 */

import { CATCH_RAW_MODIFIER } from '@shared/constants';
import { Btn } from '@shared/types';

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type ActionId =
  | 'moveForward'
  | 'moveBack'
  | 'moveLeft'
  | 'moveRight'
  | 'jump'
  | 'crouch'
  | 'dash'
  | 'fire'
  | 'throwGun'
  | 'grapple'
  | 'pulse'
  | 'catchRaw'
  | 'scoreboard'
  | 'menu';

export type ActionGroup = 'movement' | 'combat' | 'system';

export interface ActionDef {
  readonly id: ActionId;
  /** Short all-caps name for the rebinding UI. */
  readonly label: string;
  /** One line explaining what it does, for the same UI. */
  readonly hint: string;
  readonly group: ActionGroup;
  /**
   * Bit contributed to `InputCommand.buttons` while the action is held.
   * Zero for client-only actions (scoreboard, menu) that the simulation never sees.
   */
  readonly bit: number;
  /**
   * Essential actions can never end up with zero bindings — clearing the last one
   * restores the default instead, so a player cannot lock themselves out of moving.
   */
  readonly essential: boolean;
}

/**
 * Order is the order the rebinding screen shows them in, so it reads
 * movement → combat → system rather than alphabetically.
 */
export const ACTIONS: readonly ActionDef[] = [
  {
    id: 'moveForward',
    label: 'FORWARD',
    hint: 'Run forward.',
    group: 'movement',
    bit: 0,
    essential: true,
  },
  { id: 'moveBack', label: 'BACK', hint: 'Run backward.', group: 'movement', bit: 0, essential: true },
  { id: 'moveLeft', label: 'STRAFE LEFT', hint: 'Strafe left.', group: 'movement', bit: 0, essential: true },
  { id: 'moveRight', label: 'STRAFE RIGHT', hint: 'Strafe right.', group: 'movement', bit: 0, essential: true },
  {
    id: 'jump',
    label: 'JUMP',
    hint: 'Jump, double-jump, and wall-jump. Hold to bunny-hop.',
    group: 'movement',
    bit: Btn.Jump,
    essential: true,
  },
  {
    id: 'crouch',
    label: 'CROUCH / SLIDE',
    hint: 'Crouch when slow, slide when fast. Slide downhill to build speed.',
    group: 'movement',
    bit: Btn.Crouch,
    essential: true,
  },
  {
    id: 'dash',
    label: 'DASH',
    hint: 'Directional air-dash. One extra charge while unarmed.',
    group: 'movement',
    bit: Btn.Dash,
    essential: true,
  },
  {
    id: 'grapple',
    label: 'GRAPPLE',
    hint: 'Fire the tether at a grapple anchor. Release at the apex of a swing to launch.',
    group: 'movement',
    bit: Btn.Grapple,
    essential: true,
  },
  {
    id: 'fire',
    label: 'FIRE',
    hint: 'One round. Head kills, body wounds, and the chamber is empty either way.',
    group: 'combat',
    bit: Btn.Fire,
    essential: true,
  },
  {
    id: 'throwGun',
    label: 'THROW',
    hint: 'Pass the gun along your view direction. Hold to charge a longer throw.',
    group: 'combat',
    bit: Btn.Throw,
    essential: true,
  },
  {
    id: 'catchRaw',
    label: 'CATCH RAW',
    hint: 'Hold while catching to refuse the heal and keep the round chambered.',
    group: 'combat',
    bit: Btn.CatchRaw,
    essential: false,
  },
  {
    id: 'pulse',
    label: 'PULSE',
    hint: 'Kinetic shove. Knocks a gun out of the air or out of enemy hands.',
    group: 'combat',
    bit: Btn.Pulse,
    essential: true,
  },
  // NOTE: there is deliberately no INTERACT action. Picking a gun up off the ground is
  // proximity-automatic in `sim/gun.ts::tryPickup` — you walk over it and you have it
  // (empty, per DESIGN.md §1). `Btn.Interact` remains reserved in the shared protocol,
  // but binding a key that the simulation never reads would be advertising a control
  // that does nothing.
  {
    id: 'scoreboard',
    label: 'SCOREBOARD',
    hint: 'Hold to show the round score.',
    group: 'system',
    bit: 0,
    essential: false,
  },
  { id: 'menu', label: 'MENU', hint: 'Pause and release the mouse.', group: 'system', bit: 0, essential: true },
];

export const ACTION_IDS: readonly ActionId[] = ACTIONS.map((a) => a.id);

const ACTION_BY_ID_MUT: Partial<Record<ActionId, ActionDef>> = {};
for (const a of ACTIONS) ACTION_BY_ID_MUT[a.id] = a;
export const ACTION_BY_ID = ACTION_BY_ID_MUT as Readonly<Record<ActionId, ActionDef>>;

export function isActionId(v: unknown): v is ActionId {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(ACTION_BY_ID, v);
}

// ---------------------------------------------------------------------------
// Input codes
// ---------------------------------------------------------------------------

export type InputCode = string;

/** How many alternates one action may carry. Three covers "key, alt key, gamepad". */
export const MAX_CODES_PER_ACTION = 3;

const MOUSE_CODE_RE = /^Mouse[0-4]$/;
const PAD_CODE_RE = /^Pad(?:[0-9]|1[0-9])$/;
const WHEEL_CODE_RE = /^Wheel(?:Up|Down)$/;
/** Every `KeyboardEvent.code` the spec defines is a bare identifier. */
const KEY_CODE_RE = /^[A-Za-z][A-Za-z0-9]*$/;

export function isValidCode(code: unknown): code is InputCode {
  if (typeof code !== 'string' || code.length === 0 || code.length > 32) return false;
  return (
    MOUSE_CODE_RE.test(code) || PAD_CODE_RE.test(code) || WHEEL_CODE_RE.test(code) || KEY_CODE_RE.test(code)
  );
}

export const isMouseCode = (code: InputCode): boolean => MOUSE_CODE_RE.test(code);
export const isPadCode = (code: InputCode): boolean => PAD_CODE_RE.test(code);
export const isWheelCode = (code: InputCode): boolean => WHEEL_CODE_RE.test(code);

export function mouseCode(button: number): InputCode {
  return `Mouse${Math.max(0, Math.min(4, button | 0))}`;
}

export function padCode(index: number): InputCode {
  return `Pad${Math.max(0, Math.min(19, index | 0))}`;
}

export function padIndexOf(code: InputCode): number {
  return PAD_CODE_RE.test(code) ? Number(code.slice(3)) : -1;
}

export function mouseButtonOf(code: InputCode): number {
  return MOUSE_CODE_RE.test(code) ? Number(code.slice(5)) : -1;
}

export function wheelCode(deltaY: number): InputCode {
  return deltaY < 0 ? 'WheelUp' : 'WheelDown';
}

/**
 * `KeyboardEvent.code` is layout-independent, which is what we want for WASD — a
 * player on AZERTY gets ZQSD in the same physical place. Firefox on some platforms
 * leaves `code` empty for dead keys; fall back to `key` so a binding still lands.
 */
export function keyCodeOf(e: KeyboardEvent): InputCode {
  if (e.code && KEY_CODE_RE.test(e.code)) return e.code;
  const k = e.key;
  if (k && k.length === 1 && /[A-Za-z]/.test(k)) return `Key${k.toUpperCase()}`;
  if (k && k.length === 1 && /[0-9]/.test(k)) return `Digit${k}`;
  return '';
}

const MOUSE_LABELS = ['LEFT MOUSE', 'MIDDLE MOUSE', 'RIGHT MOUSE', 'MOUSE 4', 'MOUSE 5'];

/** W3C Standard Gamepad button order, in Xbox nomenclature (the one most players read). */
const PAD_LABELS = [
  'A',
  'B',
  'X',
  'Y',
  'LB',
  'RB',
  'LT',
  'RT',
  'BACK',
  'START',
  'L3',
  'R3',
  'D-PAD UP',
  'D-PAD DOWN',
  'D-PAD LEFT',
  'D-PAD RIGHT',
  'GUIDE',
  'PAD 17',
  'PAD 18',
  'PAD 19',
];

const KEY_LABELS: Record<string, string> = {
  Space: 'SPACE',
  Escape: 'ESC',
  Tab: 'TAB',
  Enter: 'ENTER',
  Backspace: 'BKSP',
  CapsLock: 'CAPS',
  ShiftLeft: 'L SHIFT',
  ShiftRight: 'R SHIFT',
  ControlLeft: 'L CTRL',
  ControlRight: 'R CTRL',
  AltLeft: 'L ALT',
  AltRight: 'R ALT',
  MetaLeft: 'L META',
  MetaRight: 'R META',
  ArrowUp: 'UP',
  ArrowDown: 'DOWN',
  ArrowLeft: 'LEFT',
  ArrowRight: 'RIGHT',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  Backquote: '`',
  IntlBackslash: '\\',
  ContextMenu: 'MENU',
  PrintScreen: 'PRTSC',
  ScrollLock: 'SCRLK',
  Pause: 'PAUSE',
  Insert: 'INS',
  Delete: 'DEL',
  Home: 'HOME',
  End: 'END',
  PageUp: 'PG UP',
  PageDown: 'PG DN',
  NumLock: 'NUM',
};

/** Human-readable name for a code. Used by the rebinding screen and the pause hints. */
export function describeCode(code: InputCode): string {
  if (!code) return '—';
  if (MOUSE_CODE_RE.test(code)) return MOUSE_LABELS[Number(code.slice(5))] ?? code;
  if (WHEEL_CODE_RE.test(code)) return code === 'WheelUp' ? 'WHEEL UP' : 'WHEEL DOWN';
  if (PAD_CODE_RE.test(code)) return PAD_LABELS[Number(code.slice(3))] ?? code;
  const named = KEY_LABELS[code];
  if (named) return named;
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return `NUM ${code.slice(6).toUpperCase()}`;
  if (/^F\d{1,2}$/.test(code)) return code;
  return code.toUpperCase();
}

/** "L SHIFT / LB" — the whole binding list for one action, for a hint line. */
export function describeBinding(codes: readonly InputCode[]): string {
  if (codes.length === 0) return 'UNBOUND';
  return codes.map(describeCode).join(' / ');
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

export type Bindings = Record<ActionId, InputCode[]>;

/**
 * Defaults.
 *
 * Shift is deliberately both DASH and CATCH RAW: a dash is an edge and a raw catch is a
 * hold, so one key carries both without ambiguity, and "hold shift to refuse the heal"
 * lands on the same finger that is already doing the aggressive thing. `CATCH_RAW_MODIFIER`
 * is the shared constant so the sim's documentation and this table can never disagree.
 */
export const DEFAULT_BINDINGS: Readonly<Bindings> = Object.freeze({
  moveForward: ['KeyW', 'ArrowUp'],
  moveBack: ['KeyS', 'ArrowDown'],
  moveLeft: ['KeyA', 'ArrowLeft'],
  moveRight: ['KeyD', 'ArrowRight'],
  jump: ['Space', 'Pad0'],
  crouch: ['ControlLeft', 'KeyC', 'Pad1'],
  dash: [CATCH_RAW_MODIFIER, 'Pad4'],
  fire: ['Mouse0', 'Pad7'],
  throwGun: ['Mouse2', 'KeyG', 'Pad6'],
  grapple: ['KeyE', 'Mouse1', 'Pad5'],
  pulse: ['KeyF', 'KeyQ', 'Pad2'],
  catchRaw: [CATCH_RAW_MODIFIER, 'Pad4'],
  scoreboard: ['Tab', 'Pad8'],
  menu: ['Escape', 'Pad9'],
} satisfies Bindings);

export function cloneBindings(b: Readonly<Bindings>): Bindings {
  const out = {} as Bindings;
  for (const id of ACTION_IDS) out[id] = (b[id] ?? []).slice();
  return out;
}

export function defaultBindings(): Bindings {
  return cloneBindings(DEFAULT_BINDINGS);
}

/**
 * Coerce anything that came out of localStorage into a usable table.
 *
 * Persisted settings are attacker-adjacent (any script on the page can write them) and,
 * more prosaically, they outlive schema changes. So this never trusts the input: unknown
 * actions are dropped, invalid codes are dropped, duplicates within an action collapse,
 * over-long lists are truncated, and an essential action that ends up empty gets its
 * default back rather than being left unplayable.
 */
export function normalizeBindings(raw: unknown): Bindings {
  const out = defaultBindings();
  if (!raw || typeof raw !== 'object') return out;
  const src = raw as Record<string, unknown>;

  for (const id of ACTION_IDS) {
    if (!Object.prototype.hasOwnProperty.call(src, id)) continue;
    const list = src[id];
    if (!Array.isArray(list)) continue;

    const codes: InputCode[] = [];
    for (const entry of list) {
      if (!isValidCode(entry)) continue;
      if (codes.includes(entry)) continue;
      codes.push(entry);
      if (codes.length >= MAX_CODES_PER_ACTION) break;
    }

    if (codes.length === 0 && ACTION_BY_ID[id].essential) continue; // keep the default
    out[id] = codes;
  }

  return out;
}

/** Every action currently answering to `code`, in table order. */
export function actionsForCode(b: Readonly<Bindings>, code: InputCode): ActionId[] {
  const hits: ActionId[] = [];
  for (const id of ACTION_IDS) {
    if ((b[id] ?? []).includes(code)) hits.push(id);
  }
  return hits;
}

/**
 * Bind `code` to `action` at `slot`, returning a new table.
 *
 * Conflicts are *not* silently allowed except where they are deliberate: DASH and
 * CATCH RAW share a key by design, and both are held-state, so `allowShared` lets the
 * caller opt in. Everything else steals the code from whoever had it, which is the
 * behaviour every shooter has trained players to expect.
 */
export function setBinding(
  b: Readonly<Bindings>,
  action: ActionId,
  slot: number,
  code: InputCode | null,
  allowShared: readonly ActionId[] = [],
): Bindings {
  const out = cloneBindings(b);
  if (!isActionId(action)) return out;

  const codes = out[action];
  const index = Math.max(0, Math.min(MAX_CODES_PER_ACTION - 1, slot | 0));

  if (code === null || !isValidCode(code)) {
    if (index < codes.length) codes.splice(index, 1);
    if (codes.length === 0 && ACTION_BY_ID[action].essential) {
      out[action] = (DEFAULT_BINDINGS[action] ?? []).slice();
    }
    return out;
  }

  // Steal it from any other action that is not explicitly allowed to share.
  for (const other of ACTION_IDS) {
    if (other === action) continue;
    if (allowShared.includes(other)) continue;
    const list = out[other];
    const at = list.indexOf(code);
    if (at >= 0) {
      list.splice(at, 1);
      if (list.length === 0 && ACTION_BY_ID[other].essential) {
        out[other] = (DEFAULT_BINDINGS[other] ?? []).slice();
      }
    }
  }

  const dup = codes.indexOf(code);
  if (dup >= 0 && dup !== index) codes.splice(dup, 1);
  if (index < codes.length) codes[index] = code;
  else codes.push(code);
  if (codes.length > MAX_CODES_PER_ACTION) codes.length = MAX_CODES_PER_ACTION;

  return out;
}

/**
 * Reverse index: code -> actions. Rebuilt whenever the table changes, then hit once per
 * key event, so the hot path never walks the action list.
 */
export class BindingMap {
  private table: Bindings;
  private readonly byCode = new Map<InputCode, ActionId[]>();
  /** Actions that contribute a `Btn` bit, precomputed so `sample()` is a flat loop. */
  private readonly bitActions: { action: ActionId; bit: number }[] = [];

  constructor(bindings: Readonly<Bindings> = DEFAULT_BINDINGS) {
    this.table = cloneBindings(bindings);
    for (const a of ACTIONS) {
      if (a.bit !== 0) this.bitActions.push({ action: a.id, bit: a.bit });
    }
    this.rebuild();
  }

  get bindings(): Readonly<Bindings> {
    return this.table;
  }

  set(bindings: Readonly<Bindings>): void {
    this.table = cloneBindings(bindings);
    this.rebuild();
  }

  codesFor(action: ActionId): readonly InputCode[] {
    return this.table[action] ?? [];
  }

  actionsFor(code: InputCode): readonly ActionId[] | undefined {
    return this.byCode.get(code);
  }

  /** Actions whose held state maps onto an `InputCommand.buttons` bit. */
  get buttonActions(): readonly { action: ActionId; bit: number }[] {
    return this.bitActions;
  }

  private rebuild(): void {
    this.byCode.clear();
    for (const id of ACTION_IDS) {
      for (const code of this.table[id] ?? []) {
        const list = this.byCode.get(code);
        if (list) list.push(id);
        else this.byCode.set(code, [id]);
      }
    }
  }
}
